import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import pickingVentaService, { type PickingEvento } from '../../../services/pickingVentaService';
import { getApiErrorMessage } from '../../../utils/apiError';
import { formatPickingDateTimeAr } from '../../../utils/formatters';
import { formatCantidadPicking } from '../pickingHelpers';

interface PickingEventosDialogProps {
  pedidoId: number | null;
  titulo: string;
  onClose: () => void;
}

const headSx = { fontWeight: 600, bgcolor: '#f5f5f5' };

const PickingEventosDialog: React.FC<PickingEventosDialogProps> = ({ pedidoId, titulo, onClose }) => {
  const [eventos, setEventos] = useState<PickingEvento[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pedidoId) return;
    let cancelado = false;
    setLoading(true);
    setError(null);
    pickingVentaService
      .getEventos(pedidoId)
      .then((data) => {
        if (!cancelado) setEventos(data);
      })
      .catch((err: unknown) => {
        if (!cancelado) setError(getApiErrorMessage(err, 'No se pudieron cargar los eventos del picking'));
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [pedidoId]);

  return (
    <Dialog open={Boolean(pedidoId)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Eventos del picking — {titulo}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : eventos.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Este pedido todavía no tiene eventos de picking.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Fecha y hora</TableCell>
                  <TableCell sx={headSx}>Evento</TableCell>
                  <TableCell sx={headSx}>Código</TableCell>
                  <TableCell sx={headSx}>Descripción</TableCell>
                  <TableCell sx={headSx} align="right">Cantidad</TableCell>
                  <TableCell sx={headSx}>Usuario</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eventos.map((ev) => (
                  <TableRow key={ev.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatPickingDateTimeAr(ev.fechaHora)}</TableCell>
                    <TableCell>{ev.evento || '-'}</TableCell>
                    <TableCell>{ev.codigoArticulo || '-'}</TableCell>
                    <TableCell>{ev.descripcion || '-'}</TableCell>
                    <TableCell align="right">
                      {ev.cantidad != null ? formatCantidadPicking(ev.cantidad) : '-'}
                    </TableCell>
                    <TableCell>{ev.usuario || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PickingEventosDialog;
